// docs/149 — a dyed clothing piece as the body wears it. Its worn layers (`itemArt(def).worn`) are dyed through
// the same table the hero is drawn with (`dyeMapOf`, IA12), stacked in z order, and baked once per (piece, dye) into
// a PNG the inventory draws where the icon would be. The 24 px icons carry none of the dye's source colours, so the
// worn art is the only thing that can show the dye. Undyeable pieces and undyed items bake nothing.
import { dyeMapOf } from './heroKit';
import { itemArt } from './itemArt';

const baked = new Map<string, Promise<string | undefined>>();

function load(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`no art at ${url}`));
    img.src = url;
  });
}

async function bake(def: number, dye: number): Promise<string | undefined> {
  const worn = itemArt(def)?.worn;
  const map = dyeMapOf(def, dye);
  if (!worn || !map) return undefined;
  const parts = await Promise.all([...worn.parts].sort((a, b) => a.z - b.z).map(async (p) => ({ z: p.z, img: await load(p.url) })));
  const out = document.createElement('canvas');
  const size = parts[0]?.img.naturalWidth ?? 32;
  out.width = size;
  out.height = size;
  const g = out.getContext('2d');
  if (!g) return undefined;
  for (const { img } of parts) {
    const layer = document.createElement('canvas');
    layer.width = img.naturalWidth;
    layer.height = img.naturalHeight;
    const lg = layer.getContext('2d');
    if (!lg) continue;
    lg.drawImage(img, 0, 0);
    const px = lg.getImageData(0, 0, layer.width, layer.height);
    const d = px.data;
    for (let i = 0; i < d.length; i += 4) {
      if (!d[i + 3]) continue;
      const hex = [d[i]!, d[i + 1]!, d[i + 2]!].map((c) => c.toString(16).padStart(2, '0')).join('');
      const to = map.get(hex);
      if (to) [d[i], d[i + 1], d[i + 2]] = to;
    }
    lg.putImageData(px, 0, 0);
    g.drawImage(layer, 0, 0);
  }
  return out.toDataURL('image/png');
}

/** The baked PNG of `def` in `dye` (undefined when it is not dyed or cannot take the dye). Cached per piece and dye. */
export function dyedArtUrl(def: number, dye: number): Promise<string | undefined> {
  if (!dye || !dyeMapOf(def, dye)) return Promise.resolve(undefined);
  const key = `${def}:${dye}`;
  let p = baked.get(key);
  if (!p) {
    p = bake(def, dye).catch(() => undefined);
    baked.set(key, p);
  }
  return p;
}
